import { useEffect, useRef, useState } from "react";
import {
  Button,
  DismissibleTag,
  InlineLoading,
  Select,
  SelectItem,
  Tag,
  TextArea,
  TextInput,
} from "@carbon/react";
import { Attachment, Copy, TrashCan } from "@carbon/icons-react";
import i18next from "../../../../i18n";
import { useFileInputUploader } from "../../../../hooks";
import { ServiceLine, ServiceUnit } from "../../../../types";
import {
  addServiceLine,
  addServiceLineFile,
  canAddServiceLine,
  duplicateServiceLine,
  removeServiceLine,
  removeServiceLineFile,
  updateServiceLine,
} from "../../serviceDetail";

type ServiceLinesEditorProps = {
  lineId: string;
  lines: ServiceLine[];
  onChange: (lines: ServiceLine[]) => void;
};

/**
 * "Líneas a cotizar" (SCRUM-113, reworked for v2 in SCRUM-117): one numbered card per thing the
 * supplier prices, with its own detail and files. Lines belong to this quotation only and are
 * never created as catalogue items.
 */
const ServiceLinesEditor = ({ lineId, lines, onChange }: ServiceLinesEditorProps) => {
  const canAdd = canAddServiceLine(lines);
  const { mutate: uploadFile, isPending } = useFileInputUploader();
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const fileInputs = useRef<(HTMLInputElement | null)[]>([]);

  // A line added from the button gets the focus on its description.
  useEffect(() => {
    if (focusIndex === null) return;
    document.getElementById(`serviceLine-${lineId}-${focusIndex}-description`)?.focus();
    setFocusIndex(null);
  }, [focusIndex, lineId]);

  const handleFile = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingIndex(index);
    uploadFile(file, {
      onSuccess: (url: string) =>
        onChange(addServiceLineFile(lines, index, { name: file.name, fileURL: url })),
      onSettled: () => setUploadingIndex(null),
    });
  };

  return (
    <div className="order-service-lines">
      <h4 className="order-service-lines__title">
        {i18next.t("serviceDetail.linesTitle")}
        {lines.length > 0 && (
          <Tag type="blue" size="sm" className="order-service-lines__count">
            {lines.length}
          </Tag>
        )}
      </h4>
      <p className="order-service-card__help">{i18next.t("serviceDetail.linesHelp")}</p>

      {lines.map((line, index) => {
        const n = index + 1;
        return (
          <div
            key={`${lineId}-line-${index}`}
            className="order-service-line"
            role="group"
            aria-label={i18next.t("serviceDetail.lineN", { n })}
          >
            <span className="order-service-line__number" aria-hidden>
              {n}
            </span>
            <div className="order-service-line__body">
              <div className="order-service-line__main">
                <TextInput
                  id={`serviceLine-${lineId}-${index}-description`}
                  labelText={i18next.t("serviceDetail.lineDescription")}
                  aria-label={i18next.t("serviceDetail.lineDescriptionN", { n })}
                  placeholder={i18next.t("serviceDetail.lineDescriptionPlaceholder")}
                  maxLength={500}
                  value={line.description}
                  onChange={(e) =>
                    onChange(updateServiceLine(lines, index, { description: e.target.value }))
                  }
                />
                <TextInput
                  id={`serviceLine-${lineId}-${index}-quantity`}
                  type="number"
                  min={0}
                  step="any"
                  labelText={i18next.t("serviceDetail.lineQuantity")}
                  aria-label={i18next.t("serviceDetail.lineQuantityN", { n })}
                  value={line.quantity}
                  onChange={(e) =>
                    onChange(updateServiceLine(lines, index, { quantity: e.target.value }))
                  }
                />
                <Select
                  id={`serviceLine-${lineId}-${index}-unit`}
                  labelText={i18next.t("serviceDetail.lineUnit")}
                  aria-label={i18next.t("serviceDetail.lineUnitN", { n })}
                  value={line.unit}
                  onChange={(e) =>
                    onChange(
                      updateServiceLine(lines, index, { unit: e.target.value as ServiceUnit })
                    )
                  }
                >
                  {Object.values(ServiceUnit).map((unit) => (
                    <SelectItem
                      key={unit}
                      value={unit}
                      text={i18next.t(`serviceDetail.unit.${unit}`)}
                    />
                  ))}
                </Select>
                <div className="order-service-line__actions">
                  <Button
                    kind="ghost"
                    size="md"
                    hasIconOnly
                    renderIcon={Copy}
                    iconDescription={i18next.t("serviceDetail.duplicateLineN", { n })}
                    disabled={!canAdd}
                    onClick={() => onChange(duplicateServiceLine(lines, index))}
                  />
                  <Button
                    kind="ghost"
                    size="md"
                    hasIconOnly
                    renderIcon={Attachment}
                    iconDescription={i18next.t("serviceDetail.attachToLineN", { n })}
                    disabled={isPending}
                    onClick={() => fileInputs.current[index]?.click()}
                  />
                  <input
                    ref={(el) => (fileInputs.current[index] = el)}
                    type="file"
                    hidden
                    data-testid={`serviceLine-${lineId}-${index}-file`}
                    onChange={handleFile(index)}
                  />
                  <Button
                    kind="ghost"
                    size="md"
                    hasIconOnly
                    renderIcon={TrashCan}
                    iconDescription={i18next.t("serviceDetail.removeLineN", { n })}
                    onClick={() => onChange(removeServiceLine(lines, index))}
                  />
                </div>
              </div>
              <TextArea
                id={`serviceLine-${lineId}-${index}-note`}
                rows={2}
                maxLength={2000}
                labelText={
                  <>
                    {i18next.t("serviceDetail.lineNote")}
                    <span className="order-service-card__optional">
                      {" "}
                      {i18next.t("serviceDetail.optional")}
                    </span>
                  </>
                }
                placeholder={i18next.t("serviceDetail.lineNotePlaceholder")}
                value={line.note ?? ""}
                onChange={(e) =>
                  onChange(updateServiceLine(lines, index, { note: e.target.value }))
                }
              />
              {uploadingIndex === index && (
                <InlineLoading description={i18next.t("serviceDetail.addFile")} />
              )}
              {(line.files ?? []).length > 0 && (
                <div className="order-service-card__chips">
                  {(line.files ?? []).map((file, fileIndex) => (
                    <DismissibleTag
                      key={`${file.fileURL}-${fileIndex}`}
                      type="blue"
                      text={file.name}
                      title={i18next.t("serviceDetail.removeFile", { name: file.name })}
                      dismissTooltipLabel={i18next.t("serviceDetail.removeFile", {
                        name: file.name,
                      })}
                      onClose={() =>
                        onChange(removeServiceLineFile(lines, index, fileIndex))
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <Button
        kind="ghost"
        size="lg"
        className="order-service-lines__add"
        disabled={!canAdd}
        onClick={() => {
          onChange(addServiceLine(lines));
          setFocusIndex(lines.length);
        }}
      >
        {i18next.t(lines.length ? "serviceDetail.addAnotherLine" : "serviceDetail.addFirstLine")}
      </Button>
    </div>
  );
};

export default ServiceLinesEditor;
